import Vue from 'vue'

import type Aeppic from './aeppic.js'
import type { Context } from "./contextify/index.js"
import { findClosestElement } from './utils/dom-traversal.js'

type Key = string                               // '<kind>:<name>' (scope is handled separately)

export class SharedContextApiForComponents  {
    private _unwatchers: Set<() => void> = new Set()
    
    constructor(private _element: Node, private _sharedContext: SharedContext) {}

    release() {
        // Unwatch all watchers (idempotent)
        this._unwatchers.forEach(unwatch => unwatch())
        this._unwatchers.clear()
        
        this._sharedContext = null as any
        this._element = null as any
    }

    /**
     * Store a value in the shared context
     */
    set(kindColonName: string, value: any): any
    set(scope: string, kindColonName: string, value: any): any
    set(a: string, b: any, c?: any) {
      if (c === undefined) {
        return this._sharedContext.set(this._element, a, b)
      } else {
        return this._sharedContext.setInScope(a, this._element, b, c)
      }
    }

    /**
     * Retrieve a value from the shared context
     */
    get(kindColonName: string): any
    get(scope: string, kindColonName: string): any
    get(a: string, b?: string) {
      return b === undefined
        ? this._sharedContext.get(this._element, a)
        : this._sharedContext.getInScope(a, b)
    }

    find = this.get.bind(this)

    /**
     * Retrieve all values of a specific kind
     */
    findAllOfKind(kind: string): any[]
    findAllOfKind(scope: string, kind: string): any[]
    findAllOfKind(a: string, b?: string) {
      return b === undefined
        ? this._sharedContext.findAllOfKind(this._element, a)
        : this._sharedContext.findAllOfKindInScope(a, b)
    }
    
    /**
     * Watch a value for changes
     */
    watch(kindColonName: string, callback: (newVal: any, oldVal: any) => void): () => void
    watch(scope: string, kindColonName: string, callback: (newVal: any, oldVal: any) => void): () => void
    watch(a: string, b: any, c?: any) {
      const kindColonName = c === undefined ? a : b
      const callback = c === undefined ? b : c
      const scope = c === undefined ? undefined : a
      
      // Get the unwatch function from SharedContext
      const unwatch = scope === undefined
        ? this._sharedContext.watch(this._element, kindColonName, callback)
        : this._sharedContext.watchInScope(scope, kindColonName, callback)
      
      // Create idempotent wrapper that removes itself from Set
      let called = false
      const safeUnwatch = () => {
        if (!called) {
          called = true
          unwatch()
          this._unwatchers.delete(safeUnwatch)
        }
      }
      
      this._unwatchers.add(safeUnwatch)
      return safeUnwatch
    }
}

export class SharedContext {
  // Scope element -> Map of observables for that scope
  private scopeData: WeakMap<Node, Map<Key, any>> = new WeakMap()
  
  constructor(private _aeppic: Aeppic) {}

  public forElement(el: Node): SharedContextApiForComponents {
    return new SharedContextApiForComponents(el, this)
  }

  private locateScopeElement(el: Node, { fallbackToGlobal = true } = {}): Node | null {
    // Look for explicit scope first
    const local = findClosestElement(el, "[data-aeppic-sharedscope]") as HTMLElement | null
    
    if (local) {
      return local
    }

    if (!fallbackToGlobal) {
      return null
    }

    // Find application root
    const root = findClosestElement(el, '[data-ae-version].ae-root, [data-ae-version]') as HTMLElement | null
    return root
  }

  private split(kindColonName: string): [string, string] {
    const [k, n = "default"] = kindColonName.split(":")
    return [k, n]
  }

  private makeKey(kind: string, name: string) {
    return `${kind}:${name}`
  }

  private getOrCreateScopeData(scopeElement: Node): Map<Key, any> {
    if (!this.scopeData.has(scopeElement)) {
      this.scopeData.set(scopeElement, new Map())
    }
    return this.scopeData.get(scopeElement)!
  }
  
  private ensureObservable(scopeElement: Node, key: Key): any {
    const scopeMap = this.getOrCreateScopeData(scopeElement)
    
    if (!scopeMap.has(key)) {
      scopeMap.set(key, Vue.observable({}))
    }
    
    return scopeMap.get(key)
  }


  private storeValue(
    scopeElement: Node,
    kindColonName: string,
    value: any
  ): any {
    const [kind, name] = this.split(kindColonName)
    const key = this.makeKey(kind, name)
    const observable = this.ensureObservable(scopeElement, key)
    
    // Always update the existing observable to maintain references
    // Clear existing properties first, then assign new ones
    Object.keys(observable).forEach(k => delete observable[k])
    
    // Only assign if value is an object (not null/undefined)
    if (value && typeof value === 'object') {
      Object.assign(observable, value)
    }
    
    return observable
  }

  /**
   * Store a value, replacing any existing value
   */
  public set(el: Node, kindColonName: string, value: any) {
    const scopeElement = this.locateScopeElement(el)
    
    if (!scopeElement) {
      return undefined
    }
    
    return this.storeValue(scopeElement, kindColonName, value)
  }

  /**
   * Retrieve a value, creating an observable if it doesn't exist
   */
  public get(el: Node, kindColonName: string) {
    const scopeElement = this.locateScopeElement(el)
    
    if (!scopeElement) {
      return undefined
    }

    const [kind, name] = this.split(kindColonName)
    const key = this.makeKey(kind, name)
    
    return this.ensureObservable(scopeElement, key)
  }

  public find(el: Node, kindColonName: string) {
    return this.get(el, kindColonName)
  }

  /**
   * Retrieve all values of a specific kind within the element's scope
   */
  public findAllOfKind(el: Node, kind: string) {
    const scopeElement = this.locateScopeElement(el)
    
    if (!scopeElement) {
      return []
    }
    
    const scopeMap = this.scopeData.get(scopeElement)
    if (!scopeMap) {
      return []
    }
    
    const prefix = `${kind}:`
    const results: any[] = []
    
    for (const [key, observable] of scopeMap) {
      if (key.startsWith(prefix)) {
        results.push(observable)
      }
    }
    
    return results
  }

  /**
   * Store a value in a specific scope
   */
  public setInScope(
    scope: string,
    el: Node,
    kindColonName: string,
    value: any
  ) {
    // For explicit scope, we need to find the scope element by name
    const scopeElement = this.findScopeElementByName(el, scope)
    
    if (!scopeElement) {
      return undefined
    }
    
    return this.storeValue(scopeElement, kindColonName, value)
  }
  
  private findScopeElementByName(el: Node, scopeName: string): Node | null {
    if (scopeName === 'global') {
      // Find application root
      return findClosestElement(el, '[data-ae-version].ae-root, [data-ae-version]') as HTMLElement | null
    }
    
    // Look for explicit scope with matching name
    let current: Node | null = el
    while (current) {
      if ((current as HTMLElement).dataset?.aeppicSharedscope === scopeName) {
        return current
      }
      current = (current as HTMLElement).parentElement
    }
    
    return null
  }
  
  private findScopeElementByNameGlobal(scopeName: string): Node | null {
    if (scopeName === 'global') {
      // Find application root globally
      return document.querySelector('[data-ae-version].ae-root, [data-ae-version]')
    }
    
    // Find scope element with matching name globally
    return document.querySelector(`[data-aeppic-sharedscope="${scopeName}"]`)
  }

  /**
   * Retrieve a value from a specific scope
   */
  public getInScope(scope: string, kindColonName: string) {
    const scopeElement = this.findScopeElementByNameGlobal(scope)
    
    if (!scopeElement) {
      return undefined
    }
    
    const [kind, name] = this.split(kindColonName)
    const key = this.makeKey(kind, name)
    
    return this.ensureObservable(scopeElement, key)
  }

  public findInScope(scope: string, kindColonName: string) {
    return this.getInScope(scope, kindColonName)
  }

  /**
   * Retrieve all values of a specific kind within a scope
   */
  public findAllOfKindInScope(scope: string, kind: string) {
    const scopeElement = this.findScopeElementByNameGlobal(scope)
    
    if (!scopeElement) {
      return []
    }
    
    const scopeMap = this.scopeData.get(scopeElement)
    if (!scopeMap) {
      return []
    }
    
    const prefix = `${kind}:`
    const results: any[] = []
    
    for (const [key, observable] of scopeMap) {
      if (key.startsWith(prefix)) {
        results.push(observable)
      }
    }
    
    return results
  }

  /**
   * Watch a value for changes
   */
  public watch(el: Node, kindColonName: string, callback: (newVal: any, oldVal: any) => void): () => void {
    const scopeElement = this.locateScopeElement(el)
    
    if (!scopeElement) {
      // Return no-op unwatch if no scope found
      return () => {}
    }
    
    const [kind, name] = this.split(kindColonName)
    const key = this.makeKey(kind, name)
    const observable = this.ensureObservable(scopeElement, key)
    
    // Watch using Aeppic's global Vue instance
    const unwatch = this._aeppic.Vue.$watch(
      () => observable,
      callback,
      { deep: true }
    )
    
    // Return idempotent unwatch
    let called = false
    return () => {
      if (!called) {
        called = true
        unwatch()
      }
    }
  }
  
  /**
   * Watch a value in a specific scope
   */
  public watchInScope(scope: string, kindColonName: string, callback: (newVal: any, oldVal: any) => void): () => void {
    const scopeElement = this.findScopeElementByNameGlobal(scope)
    
    if (!scopeElement) {
      // Return no-op unwatch if scope not found
      return () => {}
    }
    
    const [kind, name] = this.split(kindColonName)
    const key = this.makeKey(kind, name)
    const observable = this.ensureObservable(scopeElement, key)
    
    // Watch using Aeppic's global Vue instance
    const unwatch = this._aeppic.Vue.$watch(
      () => observable,
      callback,
      { deep: true }
    )
    
    // Return idempotent unwatch
    let called = false
    return () => {
      if (!called) {
        called = true
        unwatch()
      }
    }
  }
  
  /**
   * Destroy method (no longer needed since we use Aeppic's Vue instance)
   */
  public destroy() {
    // Nothing to destroy - we use Aeppic's Vue instance for watching
  }
}